"use client";

import type {
  LeadFormExitMethod,
  LeadFormSurface,
} from "@homeinstead/analytics";
import type { LeadFormDeliveryContext } from "@homeinstead/forms/types";

import {
  buildLeadFormExitEvent,
  buildLeadFormStepEvent,
  track,
} from "@homeinstead/analytics";
import { LEAD_FORM_DELIVERY_CONTEXT } from "@homeinstead/forms/lead-form";
import { EventName } from "@homeinstead/shared";
import { useMemo } from "react";

const PAGE_ZONE = "sitebuilder";

type UseStepTrackingInput = {
  deliveryContext?: LeadFormDeliveryContext | null;
  /** `0` is a national/corporate page, used when `deliveryContext` is unset. */
  franchiseId: number;
  /** Where the step renders: in the page or on a dialog surface. */
  surface: LeadFormSurface;
  /** Sent as `form_version_id`. */
  versionId: string;
};

/**
 * Stable `leadFormStep` and `leadFormExit` callbacks for the Lead Form Step
 * and Lead Form Exit Segment events. Page location is added later by the
 * analytics source middleware.
 */
function useStepTracking({
  deliveryContext,
  franchiseId,
  surface,
  versionId,
}: UseStepTrackingInput) {
  return useMemo(() => {
    const isCorporate = deliveryContext
      ? deliveryContext === LEAD_FORM_DELIVERY_CONTEXT.national
      : franchiseId === 0;

    return {
      leadFormExit: ({
        exitMethod,
        stepName,
      }: {
        exitMethod: LeadFormExitMethod;
        stepName: string;
      }) =>
        track(
          EventName.LEAD_FORM_EXIT,
          buildLeadFormExitEvent({
            exitMethod,
            formVariant: "lfb",
            formVersionId: versionId,
            isCorporate,
            pageZone: PAGE_ZONE,
            stepName,
            surface,
          }),
        ),
      leadFormStep: ({
        stepName,
        userSelection,
      }: {
        stepName: string;
        userSelection?: Record<string, string | string[]>;
      }) =>
        track(
          EventName.LEAD_FORM_STEP,
          buildLeadFormStepEvent({
            formVariant: "lfb",
            formVersionId: versionId,
            isCorporate,
            pageZone: PAGE_ZONE,
            stepName,
            surface,
            userSelection,
          }),
        ),
    };
  }, [deliveryContext, franchiseId, surface, versionId]);
}

export { useStepTracking };
